import React from "react";
import {
  Users,
  Building,
  Crown,
  UserCog,
  MapPin,
  Package,
  Headphones,
  Phone,
  AlertCircle,
  Globe,
  ChevronRight,
  ChevronDown,
  TreePine,
  Network,
  Mail,
} from "lucide-react";
import { User, Team, getRoleIcon, getRoleColor, getRoleBg } from "./types";

interface HierarchyTabProps {
  users: User[];
  teams: Team[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

export const HierarchyTab: React.FC<HierarchyTabProps> = ({
  users,
  teams,
  loading,
  error,
  onRefresh,
}) => {
  const [expandedTeams, setExpandedTeams] = React.useState<Set<string>>(
    new Set()
  );
  const [expandedUsers, setExpandedUsers] = React.useState<Set<string>>(
    new Set()
  );

  const toggleTeamExpansion = (teamId: string) => {
    const newExpanded = new Set(expandedTeams);
    if (newExpanded.has(teamId)) {
      newExpanded.delete(teamId);
    } else {
      newExpanded.add(teamId);
    }
    setExpandedTeams(newExpanded);
  };

  const toggleUserExpansion = (userId: string) => {
    const newExpanded = new Set(expandedUsers);
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
    }
    setExpandedUsers(newExpanded);
  };

  const getTeamMembers = (teamId: string) => {
    return users.filter((user) => user.teamId === teamId);
  };

  const getSupervisor = (supervisorId: string) => {
    return users.find((user) => user.id === supervisorId);
  };

  const getUsersByRole = (role: string) => {
    return users.filter((user) => user.role === role);
  };

  const getUsersByCountry = (country: string) => {
    return users.filter((user) => user.countries.includes(country));
  };

  const getSubordinates = (supervisorId: string) => {
    return users.filter((user) => user.profile.department === supervisorId);
  };

  const getCountryFlags = (countries: string[]) => {
    const flagMap: { [key: string]: string } = {
      Albania: "🇦🇱",
      Bulgaria: "🇧🇬",
      Greece: "🇬🇷",
      Kosovo: "🇽🇰",
      Serbia: "🇷🇸",
      "The Republic of Macedonia": "🇲🇰",
    };
    return countries.map((country) => flagMap[country] || "🌍").join(" ");
  };

  const buildHierarchyTree = () => {
    // Get all countries that have users in the system
    const countriesWithUsers = availableCountries.filter(
      (country) => getUsersByCountry(country).length > 0
    );

    // Build a proper organizational tree structure
    const hierarchy = {
      admins: getUsersByRole("admin"),
      managers: getUsersByRole("manager"),
      countryManagers: getUsersByRole("country-manager"),
      countries: countriesWithUsers.map((country) => ({
        name: country,
        flag: getCountryFlags([country]),
        allUsers: getUsersByCountry(country),
        postManagers: getUsersByCountry(country).filter(
          (u) => u.role === "country-post-manager"
        ),
        callCenters: getUsersByCountry(country).filter(
          (u) => u.role === "call-center"
        ),
        postOperators: getUsersByCountry(country).filter(
          (u) => u.role === "post-operator"
        ),
        callOperators: getUsersByCountry(country).filter(
          (u) => u.role === "call-operator"
        ),
        teams: teams.filter((team) => team.countries.includes(country)),
      })),
    };
    return hierarchy;
  };

  const availableCountries = [
    "Albania",
    "Bulgaria",
    "Greece",
    "Kosovo",
    "Serbia",
    "The Republic of Macedonia",
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#264a3e]"></div>
        <span className="ml-2 text-gray-600">Loading hierarchy...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">
            Organizational Hierarchy
          </h2>
          <p className="text-sm text-gray-600">
            View the organizational structure and team relationships
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <Users className="w-8 h-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Users</p>
              <p className="text-2xl font-semibold text-gray-900">
                {users.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <Building className="w-8 h-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Teams</p>
              <p className="text-2xl font-semibold text-gray-900">
                {teams.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <Crown className="w-8 h-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Admins</p>
              <p className="text-2xl font-semibold text-gray-900">
                {getUsersByRole("admin").length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <UserCog className="w-8 h-8 text-orange-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Managers</p>
              <p className="text-2xl font-semibold text-gray-900">
                {getUsersByRole("manager").length +
                  getUsersByRole("country-manager").length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Organizational Tree Structure */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Organizational Chart
          </h3>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 mx-auto rounded-full"></div>
        </div>

        <div className="overflow-x-auto">
          <div className="relative">
            {(() => {
              const hierarchy = buildHierarchyTree();

              return (
                <div className="flex">
                  {/* System Admin - Side Panel */}
                  {hierarchy.admins.length > 0 && (
                    <div className="flex-shrink-0 mr-8">
                      <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg p-4 shadow-lg border border-purple-300 w-64">
                        <h5 className="text-center text-sm font-semibold text-purple-800 mb-3">
                          System Admin
                        </h5>
                        {hierarchy.admins.map((admin) => (
                          <div
                            key={admin.id}
                            className="bg-white rounded-lg shadow-md border border-purple-200 p-3 w-full text-center"
                          >
                            <div className="relative mx-auto w-12 h-12 mb-2">
                              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-700 rounded-full flex items-center justify-center shadow-md">
                                <Crown className="w-6 h-6 text-white" />
                              </div>
                              <div className="absolute -top-1 -right-1 bg-purple-600 text-white text-xs px-1 py-0.5 rounded-full shadow-sm">
                                SYS
                              </div>
                            </div>
                            <h4 className="font-semibold text-gray-900 text-sm">
                              {admin.profile.firstName} {admin.profile.lastName}
                            </h4>
                            <p className="text-purple-600 font-medium text-xs">
                              @{admin.username}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Main Hierarchy Tree */}
                  <div className="flex-1">
                    {/* Level 1: Manager (Top of hierarchy) */}
                    {hierarchy.managers.length > 0 && (
                      <div className="flex justify-center mb-8">
                        {hierarchy.managers.map((manager) => (
                          <div
                            key={manager.id}
                            className="bg-white rounded-lg shadow-lg border-2 border-blue-200 p-6 w-80 transform hover:shadow-xl transition-shadow duration-200"
                          >
                            <div className="flex items-center space-x-4 mb-4">
                              <div className="relative">
                                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center shadow-lg">
                                  <UserCog className="w-8 h-8 text-white" />
                                </div>
                                <div className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs px-2 py-1 rounded-full shadow-md font-bold">
                                  MGR
                                </div>
                              </div>
                              <div className="flex-1">
                                <h4 className="font-bold text-gray-900 text-lg">
                                  {manager.profile.firstName}{" "}
                                  {manager.profile.lastName}
                                </h4>
                                <p className="text-blue-600 font-medium text-sm">
                                  @{manager.username}
                                </p>
                                <div className="mt-1">
                                  <span className="bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full font-semibold">
                                    General Manager
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="space-y-2 text-sm text-gray-600">
                              {manager.profile.phone && (
                                <div className="flex items-center space-x-2">
                                  <Phone className="w-4 h-4 text-blue-500" />
                                  <span className="font-medium">
                                    {manager.profile.phone}
                                  </span>
                                </div>
                              )}
                              {manager.profile.department && (
                                <div className="flex items-center space-x-2">
                                  <Building className="w-4 h-4 text-blue-500" />
                                  <span className="font-medium">
                                    {manager.profile.department}
                                  </span>
                                </div>
                              )}
                              <div className="flex items-center space-x-2">
                                <Mail className="w-4 h-4 text-blue-500" />
                                <span className="font-medium truncate">
                                  {manager.email}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Globe className="w-4 h-4 text-blue-500" />
                                <span className="font-medium">
                                  {manager.countries.join(", ")}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Level 2: Country Managers */}
                    {hierarchy.countryManagers.length > 0 && (
                      <div className="flex justify-center space-x-8 mb-8">
                        {hierarchy.countryManagers.map((user) => (
                          <div
                            key={user.id}
                            className="bg-white rounded-lg shadow-lg border-2 border-green-200 p-6 w-80 transform hover:shadow-xl transition-shadow duration-200"
                          >
                            <div className="flex items-center space-x-4 mb-4">
                              <div className="relative">
                                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center shadow-lg">
                                  <MapPin className="w-8 h-8 text-white" />
                                </div>
                                <div className="absolute -top-1 -right-1 bg-green-600 text-white text-xs px-2 py-1 rounded-full shadow-md font-bold">
                                  CM
                                </div>
                              </div>
                              <div className="flex-1">
                                <h4 className="font-bold text-gray-900 text-lg">
                                  {user.profile.firstName}{" "}
                                  {user.profile.lastName}
                                </h4>
                                <p className="text-green-600 font-medium text-sm">
                                  @{user.username}
                                </p>
                                <div className="mt-1">
                                  <span className="bg-green-100 text-green-800 text-xs px-3 py-1 rounded-full font-semibold">
                                    {user.countries.join(", ")} Manager
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="space-y-2 text-sm text-gray-600">
                              {user.profile.phone && (
                                <div className="flex items-center space-x-2">
                                  <Phone className="w-4 h-4 text-green-500" />
                                  <span className="font-medium">
                                    {user.profile.phone}
                                  </span>
                                </div>
                              )}
                              {user.profile.department && (
                                <div className="flex items-center space-x-2">
                                  <Building className="w-4 h-4 text-green-500" />
                                  <span className="font-medium">
                                    {user.profile.department}
                                  </span>
                                </div>
                              )}
                              <div className="flex items-center space-x-2">
                                <Mail className="w-4 h-4 text-green-500" />
                                <span className="font-medium truncate">
                                  {user.email}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Globe className="w-4 h-4 text-green-500" />
                                <span className="font-medium text-lg">
                                  {user.countries.join(", ")}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Level 3: Countries with Teams and Accounts */}
                    <div className="flex justify-center space-x-8">
                      {hierarchy.countries.map((country) => (
                        <div
                          key={country.name}
                          className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 shadow-lg border border-gray-200 w-96"
                        >
                          {/* Country Header */}
                          <div className="text-center mb-4">
                            <h5 className="font-bold text-gray-800 text-sm mb-2">
                              {country.flag} {country.name}
                            </h5>
                            <div className="w-16 h-0.5 bg-gradient-to-r from-green-400 to-green-600 mx-auto rounded-full"></div>
                          </div>

                          {/* Country Accounts Summary */}
                          <div className="mb-4 p-3 bg-white rounded-lg border border-gray-200">
                            <div className="text-center">
                              <div className="text-lg font-bold text-gray-900">
                                {country.allUsers.length}
                              </div>
                              <div className="text-xs text-gray-500">
                                Total Accounts
                              </div>
                            </div>
                          </div>

                          {/* All Country Accounts */}
                          <div className="space-y-3">
                            {country.allUsers.map((user) => {
                              const RoleIcon = getRoleIcon(user.role);
                              const roleColor = getRoleColor(user.role);
                              const roleBg = getRoleBg(user.role);

                              return (
                                <div
                                  key={user.id}
                                  className="bg-white rounded-lg shadow-md border border-gray-200 p-3 hover:shadow-lg transition-shadow duration-200"
                                >
                                  <div className="flex items-center space-x-3">
                                    <div
                                      className={`w-8 h-8 ${roleBg} rounded-full flex items-center justify-center shadow-md`}
                                    >
                                      <RoleIcon className="w-4 h-4 text-white" />
                                    </div>
                                    <div className="flex-1">
                                      <h6 className="font-semibold text-gray-900 text-sm">
                                        {user.profile.firstName}{" "}
                                        {user.profile.lastName}
                                      </h6>
                                      <p className={`${roleColor} text-xs`}>
                                        {user.role
                                          .replace("-", " ")
                                          .replace(/\b\w/g, (l) =>
                                            l.toUpperCase()
                                          )}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-xs text-gray-500">
                                        @{user.username}
                                      </div>
                                      {user.profile.phone && (
                                        <div className="text-xs text-gray-500">
                                          {user.profile.phone}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HierarchyTab;
